import { type SkuKeyDict } from "@/off.data-schema/product";

/**
 * Converts a variant key dictionary into a string representation by concatenating keys and values
 * in alphabetical order of keys.
 * @param skuKeyDict - Object containing variant keys and their corresponding values
 * @returns A concatenated string of all key-value pairs, sorted by keys
 *
 * @example
 * const input = {
 *   "cc": "bbb",
 *   "aa": "1ee",
 *   "1g": "2ea"
 * };
 * encodeSkuKeyDict(input); // Returns "1g2eaaa1eeccbbb"
 */
export const encodeSkuKeyDict = (skuKeyDict: SkuKeyDict): string => {
  // If skuKeyDict is empty, return "mainsku"
  if (Object.keys(skuKeyDict).length === 0) {
    return "mainsku";
  }

  // Convert object to array of key-value pairs
  const entries = Object.entries(skuKeyDict);

  // Sort by keys
  // sort using ASCII / case-sensitive, like python's sorted()
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort#description
  const sortedEntries = entries.sort(([a], [b]) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  });

  // Create array of concatenated strings
  const stringParts = sortedEntries.map(([k, v]) => {
    return `${k}${v}`;
  });

  // Join all parts together
  const result = stringParts.join("");

  return result;
};

/**
 * Decodes a string representation back into a variant key dictionary.
 * Each key is 2 characters and each value is 3 characters.
 * @param encoded - The encoded string representation of variant key-value pairs
 * @returns Decoded SkuKeyDict object
 *
 * @example
 * const encoded = "1g2eaaa1eeccbbb";
 * decodeSkuKeyDict(encoded); // Returns { "1g": "2ea", "aa": "1ee", "cc": "bbb" }
 */
export const decodeSkuKeyDict = (encoded: string): SkuKeyDict => {
  const result: SkuKeyDict = {};

  if (encoded === "mainsku") {
    return result;
  }

  if (encoded.length % 5 !== 0) {
    throw new Error("Invalid encoded string format: length must be divisible by 5");
  }

  for (let i = 0; i < encoded.length; i += 5) {
    const key = encoded.slice(i, i + 2);
    const value = encoded.slice(i + 2, i + 5);
    result[key] = value;
  }

  return result;
};
